import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageSkeleton } from '../DashboardLoader';
import {
  createCateringService,
  getCateringServiceById,
  handleRestaurantApiError,
  updateCateringService,
  uploadCateringServiceImage,
} from '../../../services/restaurantApi';
import { getCateringFormulas } from '../../../services/cateringApi';
import { toastError, toastSuccess } from '../../../utils/toast';
import { CATERING_SERVICE_TYPES, DIETARY_OPTIONS, toDietaryIds } from '../../../constants/catering';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const inputStyle = {
  width: '100%', minHeight: '46px', padding: '0 18px', background: '#FBF9D9',
  border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', fontFamily: 'Poppins, sans-serif',
  fontSize: '14px', color: '#051004', outline: 'none',
};
const inputErrorStyle = { ...inputStyle, border: '1px solid #EF4C23' };
const textareaStyle = { ...inputStyle, borderRadius: '20px', padding: '14px 18px', minHeight: 'auto', resize: 'vertical' };

const fieldLabelStyle = {
  display: 'block', marginBottom: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '12px',
  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)',
};

const sectionCardStyle = { background: '#FCFBF5', borderRadius: '26px', padding: 'clamp(18px, 2.2vw, 26px)', display: 'flex', flexDirection: 'column', gap: '16px' };

const emptyForm = () => ({
  formula_id: '',
  service_name: '',
  service_description: '',
  service_type: '',
  base_price: '0',
  price_per_person: '',
  min_guests: '10',
  max_guests: '100',
  min_notice_hours: '24',
  delivery_available: false,
  delivery_fee: '0',
  setup_available: false,
  setup_fee: '0',
  staff_available: false,
  staff_fee_per_hour: '0',
  equipment_rental: false,
  equipment_fee: '0',
  dietary_options: [],
  cancellation_policy: '',
  deposit_percentage: '30',
  certifications: [],
  image_url: '',
  is_active: true,
});

const toNumberOrNull = (value) => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const CateringServiceOfferDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isCreateMode = serviceId === 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [validationErrors, setValidationErrors] = useState({});
  const [formulas, setFormulas] = useState([]);

  useEffect(() => {
    getCateringFormulas().then((data) => setFormulas(Array.isArray(data) ? data : [])).catch(() => setFormulas([]));
  }, []);

  useEffect(() => {
    const loadService = async () => {
      if (isCreateMode) {
        setForm(emptyForm());
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const selected = await getCateringServiceById(serviceId);

        if (!selected) {
          setError("Cette offre traiteur n'existe pas ou n'est plus accessible.");
          return;
        }

        setForm({
          formula_id: selected.formula_id != null ? String(selected.formula_id) : '',
          service_name: selected.service_name || '',
          service_description: selected.service_description || '',
          service_type: selected.service_type || '',
          base_price: selected.base_price != null ? String(selected.base_price) : '0',
          price_per_person: selected.price_per_person != null ? String(selected.price_per_person) : '',
          min_guests: selected.min_guests != null ? String(selected.min_guests) : '10',
          max_guests: selected.max_guests != null ? String(selected.max_guests) : '',
          min_notice_hours: selected.min_notice_hours != null ? String(selected.min_notice_hours) : '24',
          delivery_available: Boolean(selected.delivery_available),
          delivery_fee: selected.delivery_fee != null ? String(selected.delivery_fee) : '0',
          setup_available: Boolean(selected.setup_available),
          setup_fee: selected.setup_fee != null ? String(selected.setup_fee) : '0',
          staff_available: Boolean(selected.staff_available),
          staff_fee_per_hour: selected.staff_fee_per_hour != null ? String(selected.staff_fee_per_hour) : '0',
          equipment_rental: Boolean(selected.equipment_rental),
          equipment_fee: selected.equipment_fee != null ? String(selected.equipment_fee) : '0',
          dietary_options: toDietaryIds(selected.dietary_options),
          cancellation_policy: selected.cancellation_policy || '',
          deposit_percentage: selected.deposit_percentage != null ? String(selected.deposit_percentage) : '30',
          certifications: Array.isArray(selected.certifications) ? selected.certifications : [],
          image_url: selected.image_url || '',
          is_active: selected.is_active !== false,
        });
      } catch (loadError) {
        setError(handleRestaurantApiError(loadError));
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [isCreateMode, serviceId]);

  const pageTitle = useMemo(
    () => (isCreateMode ? 'Créer une offre de service traiteur' : 'Détail de l’offre traiteur'),
    [isCreateMode]
  );

  const onFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.service_name.trim()) {
      nextErrors.service_name = "Le nom de l'offre est requis.";
    }
    const minGuests = Number(form.min_guests);
    const maxGuests = form.max_guests ? Number(form.max_guests) : null;
    if (!Number.isFinite(minGuests) || minGuests <= 0) {
      nextErrors.min_guests = "Le nombre minimum d'invités doit être supérieur à 0.";
    }
    if (maxGuests != null && (!Number.isFinite(maxGuests) || maxGuests < minGuests)) {
      nextErrors.max_guests = 'Le maximum doit être supérieur ou égal au minimum.';
    }
    const base = Number(form.base_price);
    if (!Number.isFinite(base) || base < 0) {
      nextErrors.base_price = 'Le prix de base est invalide.';
    }
    if (form.price_per_person) {
      const ppp = Number(form.price_per_person);
      if (!Number.isFinite(ppp) || ppp <= 0) {
        nextErrors.price_per_person = 'Le prix par personne doit être supérieur à 0.';
      }
    }
    const deposit = Number(form.deposit_percentage);
    if (!Number.isFinite(deposit) || deposit < 0 || deposit > 100) {
      nextErrors.deposit_percentage = "L'acompte doit être compris entre 0 et 100.";
    }
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    formula_id: form.formula_id ? Number(form.formula_id) : null,
    service_name: form.service_name.trim(),
    service_description: form.service_description?.trim() || null,
    service_type: form.service_type || null,
    base_price: Number(form.base_price || 0),
    price_per_person: toNumberOrNull(form.price_per_person),
    min_guests: Number(form.min_guests),
    max_guests: toNumberOrNull(form.max_guests),
    min_notice_hours: toNumberOrNull(form.min_notice_hours),
    delivery_available: form.delivery_available,
    delivery_fee: form.delivery_available ? Number(form.delivery_fee || 0) : 0,
    setup_available: form.setup_available,
    setup_fee: form.setup_available ? Number(form.setup_fee || 0) : 0,
    staff_available: form.staff_available,
    staff_fee_per_hour: form.staff_available ? Number(form.staff_fee_per_hour || 0) : 0,
    equipment_rental: form.equipment_rental,
    equipment_fee: form.equipment_rental ? Number(form.equipment_fee || 0) : 0,
    dietary_options: form.dietary_options,
    cancellation_policy: form.cancellation_policy?.trim() || null,
    deposit_percentage: Number(form.deposit_percentage || 30),
    certifications: form.certifications.filter((c) => c.trim()),
    image_url: form.image_url?.trim() || null,
    is_active: form.is_active,
    currency: 'EUR',
  });

  const onUploadClick = () => fileInputRef.current?.click();

  const onFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toastError('Veuillez sélectionner un fichier image valide.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toastError("L'image ne doit pas dépasser 5 Mo.");
      return;
    }

    try {
      setUploadingImage(true);
      const imageUrl = await uploadCateringServiceImage(file);
      onFieldChange('image_url', imageUrl);
      toastSuccess('Image importée.');
    } catch (uploadError) {
      toastError(handleRestaurantApiError(uploadError));
    } finally {
      setUploadingImage(false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (isCreateMode) {
        await createCateringService(payload);
        toastSuccess('Offre traiteur créée avec succès.');
      } else {
        await updateCateringService(Number(serviceId), payload);
        toastSuccess('Offre traiteur mise à jour avec succès.');
      }
      navigate('/management/catering');
    } catch (saveError) {
      const message = handleRestaurantApiError(saveError);
      setError(message);
      toastError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      <Link
        to="/management/catering"
        className="inline-flex items-center gap-1.5"
        style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: PRIMARY, width: 'fit-content' }}
      >
        <ArrowLeft size={15} />
        Retour à la liste des offres
      </Link>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
          {isCreateMode ? 'Nouvelle offre' : 'Modifier l’offre'}
        </span>
        <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>{pageTitle}</h1>
        <p style={{ margin: 0, fontSize: '14.5px', color: 'rgba(5,16,4,0.65)' }}>Créez et éditez vos offres/menu traiteur avec image, prix et options de service.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          {typeof error === 'string' ? error : 'Une erreur est survenue.'}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(14px, 1.8vw, 22px)' }}>
        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label style={fieldLabelStyle}>Nom de l'offre *</label>
              <input
                type="text"
                value={form.service_name}
                onChange={(e) => onFieldChange('service_name', e.target.value)}
                style={validationErrors.service_name ? inputErrorStyle : inputStyle}
                placeholder="Ex: Offre Mariage Premium"
              />
              {validationErrors.service_name && (
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.service_name}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label style={fieldLabelStyle}>Description</label>
              <textarea
                rows={4}
                value={form.service_description}
                onChange={(e) => onFieldChange('service_description', e.target.value)}
                style={textareaStyle}
                placeholder="Décrivez l'offre et son contenu."
              />
            </div>

            <div>
              <label style={fieldLabelStyle}>Format de l'offre</label>
              <select value={form.service_type} onChange={(e) => onFieldChange('service_type', e.target.value)} style={inputStyle}>
                <option value="">Sélectionner</option>
                {CATERING_SERVICE_TYPES.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={fieldLabelStyle}>Formule namke correspondante</label>
              <select value={form.formula_id} onChange={(e) => onFieldChange('formula_id', e.target.value)} style={inputStyle}>
                <option value="">Aucune / offre spécifique</option>
                {formulas.map((formula) => (
                  <option key={formula.id} value={formula.id}>{formula.name}</option>
                ))}
              </select>
              <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>
                Rattachez l'offre à une formule namke pour recevoir les demandes faites depuis cette formule sur la page Traiteur. Ce sont toujours vos prix qui s'appliquent.
              </p>
            </div>

            <div>
              <label style={fieldLabelStyle}>Statut</label>
              <select value={form.is_active ? 'active' : 'inactive'} onChange={(e) => onFieldChange('is_active', e.target.value === 'active')} style={inputStyle}>
                <option value="active">Active (visible des clients)</option>
                <option value="inactive">Inactive (masquée)</option>
              </select>
            </div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Tarification &amp; capacité</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label style={fieldLabelStyle}>Prix de base (€)</label>
              <input type="number" min="0" step="0.01" value={form.base_price} onChange={(e) => onFieldChange('base_price', e.target.value)} style={validationErrors.base_price ? inputErrorStyle : inputStyle} />
              {validationErrors.base_price && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.base_price}</p>}
            </div>
            <div>
              <label style={fieldLabelStyle}>Prix / personne (€)</label>
              <input type="number" min="0" step="0.01" value={form.price_per_person} onChange={(e) => onFieldChange('price_per_person', e.target.value)} style={validationErrors.price_per_person ? inputErrorStyle : inputStyle} />
              {validationErrors.price_per_person && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.price_per_person}</p>}
            </div>
            <div>
              <label style={fieldLabelStyle}>Invités min *</label>
              <input type="number" min="1" value={form.min_guests} onChange={(e) => onFieldChange('min_guests', e.target.value)} style={validationErrors.min_guests ? inputErrorStyle : inputStyle} />
              {validationErrors.min_guests && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.min_guests}</p>}
            </div>
            <div>
              <label style={fieldLabelStyle}>Invités max</label>
              <input type="number" min="1" value={form.max_guests} onChange={(e) => onFieldChange('max_guests', e.target.value)} style={validationErrors.max_guests ? inputErrorStyle : inputStyle} />
              {validationErrors.max_guests && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.max_guests}</p>}
            </div>
          </div>
          <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>
            Prix payé par le client = prix de base + prix / personne × convives + options qu'il choisit. Laissez le prix / personne vide pour un forfait.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label style={fieldLabelStyle}>Délai de prévenance minimum (heures)</label>
              <input type="number" min="0" value={form.min_notice_hours} onChange={(e) => onFieldChange('min_notice_hours', e.target.value)} style={inputStyle} />
              <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Les clients ne peuvent pas demander un événement plus proche.</p>
            </div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Options payantes</h2>
          <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Le client coche celles qu'il veut ; leur prix s'ajoute au total (le personnel est facturé à l'heure).</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'delivery_available', feeKey: 'delivery_fee', label: 'Livraison', placeholder: 'Frais de livraison' },
              { key: 'setup_available', feeKey: 'setup_fee', label: 'Installation', placeholder: 'Frais d’installation' },
              { key: 'equipment_rental', feeKey: 'equipment_fee', label: 'Matériel', placeholder: 'Frais de matériel' },
              { key: 'staff_available', feeKey: 'staff_fee_per_hour', label: 'Personnel de service', placeholder: 'Coût / heure' },
            ].map((opt) => (
              <div key={opt.key}>
                <label className="inline-flex items-center gap-2" style={{ marginBottom: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: '#051004' }}>
                  <input
                    type="checkbox"
                    checked={form[opt.key]}
                    onChange={(e) => onFieldChange(opt.key, e.target.checked)}
                    style={{ width: '17px', height: '17px', accentColor: PRIMARY }}
                  />
                  {opt.label}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form[opt.feeKey]}
                  onChange={(e) => onFieldChange(opt.feeKey, e.target.value)}
                  disabled={!form[opt.key]}
                  style={{ ...inputStyle, opacity: form[opt.key] ? 1 : 0.5 }}
                  placeholder={opt.placeholder}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Régimes pris en charge</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {DIETARY_OPTIONS.map((opt) => (
              <label key={opt.id} className="inline-flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', color: '#051004' }}>
                <input
                  type="checkbox"
                  checked={form.dietary_options.includes(opt.id)}
                  onChange={(e) => onFieldChange('dietary_options', e.target.checked
                    ? [...form.dietary_options, opt.id]
                    : form.dietary_options.filter((id) => id !== opt.id))}
                  style={{ width: '17px', height: '17px', accentColor: PRIMARY }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Annulation &amp; acompte</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label style={fieldLabelStyle}>Politique d'annulation</label>
              <textarea
                rows={2}
                value={form.cancellation_policy}
                onChange={(e) => onFieldChange('cancellation_policy', e.target.value)}
                style={textareaStyle}
                placeholder="Ex: Annulation gratuite jusqu'à 48h avant l'événement..."
              />
            </div>
            <div>
              <label style={fieldLabelStyle}>Acompte demandé (%)</label>
              <input type="number" min="0" max="100" step="1" value={form.deposit_percentage} onChange={(e) => onFieldChange('deposit_percentage', e.target.value)} style={validationErrors.deposit_percentage ? inputErrorStyle : inputStyle} />
              {validationErrors.deposit_percentage && <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#A32C0F' }}>{validationErrors.deposit_percentage}</p>}
              <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Pourcentage demandé à la confirmation de la réservation.</p>
            </div>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Certifications</h2>
          <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Ex: Certifié Halal, Agréé sanitaire, Bio... Affichées telles quelles sur votre fiche publique.</p>
          <div className="flex flex-wrap gap-2">
            {form.certifications.map((cert, index) => (
              <span key={index} className="inline-flex items-center gap-1.5" style={{ background: '#DDEECB', color: PRIMARY, fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, padding: '6px 12px 6px 14px', borderRadius: '999px' }}>
                {cert}
                <button
                  type="button"
                  onClick={() => onFieldChange('certifications', form.certifications.filter((_, i) => i !== index))}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: PRIMARY, fontWeight: 700, lineHeight: 1 }}
                  aria-label={`Retirer ${cert}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              id="new-certification-input"
              placeholder="Ajouter une certification..."
              style={{ ...inputStyle, flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const value = e.target.value.trim();
                  if (value) {
                    onFieldChange('certifications', [...form.certifications, value]);
                    e.target.value = '';
                  }
                }
              }}
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('new-certification-input');
                const value = input?.value.trim();
                if (value) {
                  onFieldChange('certifications', [...form.certifications, value]);
                  input.value = '';
                }
              }}
              style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', minHeight: '46px', padding: '0 18px', borderRadius: '999px', whiteSpace: 'nowrap' }}
            >
              Ajouter
            </button>
          </div>
        </div>

        <div style={sectionCardStyle}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '19px' }}>Image de l'offre</h2>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFileChange} />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onUploadClick}
              disabled={uploadingImage}
              className="inline-flex items-center gap-2"
              style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', minHeight: '44px', padding: '0 18px', borderRadius: '999px', opacity: uploadingImage ? 0.6 : 1 }}
            >
              <Upload size={15} />
              {uploadingImage ? 'Import en cours...' : 'Importer une image'}
            </button>
            {form.image_url && (
              <button type="button" onClick={() => onFieldChange('image_url', '')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '13px', color: '#A32C0F', fontFamily: 'Archivo, sans-serif', fontWeight: 600 }}>
                Retirer l'image
              </button>
            )}
          </div>
          <div>
            <label style={fieldLabelStyle}>Ou coller une URL</label>
            <input type="url" value={form.image_url} onChange={(e) => onFieldChange('image_url', e.target.value)} style={inputStyle} placeholder="https://..." />
          </div>
          {form.image_url && (
            <img src={form.image_url} alt="Aperçu de l'offre" style={{ width: '100%', maxWidth: '420px', height: '200px', objectFit: 'cover', borderRadius: '20px' }} />
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/management/catering')}
            style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '46px', padding: '0 22px', borderRadius: '999px' }}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving || uploadingImage}
            className="inline-flex items-center gap-2"
            style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', opacity: (saving || uploadingImage) ? 0.6 : 1 }}
          >
            <Save size={16} />
            {saving ? 'Enregistrement...' : isCreateMode ? 'Créer l’offre' : 'Enregistrer'}
          </button>
        </div>
      </form>

    </div>
  );
};

export default CateringServiceOfferDetail;
